import { type Frame, toWorld, type V3 } from './geometry'
import { ccw, type Pt, segmentSpans } from './polygon'
import { outlineOf, type RoomInput, insideRoom as roomContains, shapeOf } from './room'

/**
 * Edge diffraction. When a solid stands between source and listener, sound still arrives by bending round the
 * vertical edges of the obstacle or over its top. This module finds those routes; compute.ts turns each into a path
 * whose loss follows the Fresnel number (Kurze–Anderson barrier attenuation), so low frequencies bend round
 * easily and highs are shadowed.
 *
 * Routing is done in plan view (a visibility graph over obstacle corners, solved with Dijkstra). Heights follow the
 * unfolded path, which is exact for vertical edges. Up to two ways round (one per side) plus one way over.
 */

export interface Obstacle {
  id: string
  /** Display name for debug labels. */
  name: string
  /** Footprint in plan (x, z), counter-clockwise. Convex hull for most shapes; the true outline for upright prisms. */
  plan: Pt[]
  yMin: number
  yMax: number
}

export interface DiffractionRoute {
  kind: 'around' | 'over'
  /** Stable across updates: which edges the route bends round. */
  id: string
  label: string
  /** The diffracting points in 3D, in order from the source. */
  points: V3[]
  length: number
  /** Extra path length compared with the straight line. */
  delta: number
}

const EPS_NODE = 0.08

function convexHull(pts: Pt[]): Pt[] {
  const p = [...pts].sort((a, b) => a[0] - b[0] || a[1] - b[1])
  if (p.length < 3) return p
  const cross = (o: Pt, a: Pt, b: Pt) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0])
  const lower: Pt[] = []
  for (const q of p) {
    while (lower.length >= 2 && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, q) <= 0) lower.pop()
    lower.push(q)
  }
  const upper: Pt[] = []
  for (const q of [...p].reverse()) {
    while (upper.length >= 2 && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, q) <= 0) upper.pop()
    upper.push(q)
  }
  lower.pop()
  upper.pop()
  return [...lower, ...upper]
}

/** Plan-view footprint and vertical extent of a primitive. */
export function obstacleOf(f: Frame): Obstacle {
  const s = f.shape
  const [hx, hh, hz] = f.half
  const world: V3[] = []
  const upright = Math.abs(s.rotation[0]) < 1e-3 && Math.abs(s.rotation[2]) < 1e-3
  if (s.kind === 'prism' && f.contours?.[0] && upright) {
    const ys = [s.position[1] - hh, s.position[1] + hh]
    const plan = ccw(
      f.contours[0].map(([x, z]) => {
        const w = toWorld(f, [x, 0, z])
        return [w[0], w[2]] as Pt
      }),
    )
    return { id: s.id, name: s.name ?? s.id, plan, yMin: ys[0]!, yMax: ys[1]! }
  }
  if (s.kind === 'box' || s.kind === 'prism') {
    const pts: [number, number][] =
      s.kind === 'prism' && f.contours?.[0]
        ? f.contours[0].map(([x, z]) => [x, z] as [number, number])
        : [
            [-hx, -hz],
            [hx, -hz],
            [hx, hz],
            [-hx, hz],
          ]
    for (const [x, z] of pts) for (const y of [-hh, hh]) world.push(toWorld(f, [x, y, z]))
  } else {
    // Cylinders and tubes: outer ellipse at both ends.
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2
      for (const y of [-hh, hh]) world.push(toWorld(f, [hx * Math.cos(a), y, hz * Math.sin(a)]))
    }
  }
  const plan = convexHull(world.map((p) => [p[0], p[2]] as Pt))
  return {
    id: s.id,
    name: s.name ?? s.id,
    plan: ccw(plan),
    yMin: Math.min(...world.map((p) => p[1])),
    yMax: Math.max(...world.map((p) => p[1])),
  }
}

interface Node {
  key: string
  p: Pt
  /** Obstacle the corner belongs to (null for room corners). */
  owner: Obstacle | null
}

/** Convex corners of an obstacle, nudged outward so a route can pass them without touching the solid. */
function obstacleNodes(o: Obstacle): Node[] {
  const out: Node[] = []
  const c = o.plan
  const n = c.length
  for (let i = 0; i < n; i++) {
    const prev = c[(i + n - 1) % n]!
    const p = c[i]!
    const next = c[(i + 1) % n]!
    const e1: Pt = [p[0] - prev[0], p[1] - prev[1]]
    const e2: Pt = [next[0] - p[0], next[1] - p[1]]
    if (e1[0] * e2[1] - e1[1] * e2[0] <= 1e-9) continue // reflex or straight: sound does not bend round it
    const l1 = Math.hypot(e1[0], e1[1]) || 1
    const l2 = Math.hypot(e2[0], e2[1]) || 1
    // Outward (right-hand) normals of the two edges at this corner.
    const nx = e1[1] / l1 + e2[1] / l2
    const nz = -e1[0] / l1 - e2[0] / l2
    const m = Math.hypot(nx, nz) || 1
    out.push({ key: `${o.id}#${i}`, p: [p[0] + (nx / m) * EPS_NODE, p[1] + (nz / m) * EPS_NODE], owner: o })
  }
  return out
}

/** Reflex corners of a polygon room: the corners sound has to go round to reach the next arm. */
function roomNodes(room: RoomInput): Node[] {
  if (shapeOf(room) !== 'polygon') return []
  const c = outlineOf(room)
  const n = c.length
  const out: Node[] = []
  for (let i = 0; i < n; i++) {
    const prev = c[(i + n - 1) % n]!
    const p = c[i]!
    const next = c[(i + 1) % n]!
    const e1: Pt = [p[0] - prev[0], p[1] - prev[1]]
    const e2: Pt = [next[0] - p[0], next[1] - p[1]]
    if (e1[0] * e2[1] - e1[1] * e2[0] >= -1e-9) continue // convex corner of the room
    const l1 = Math.hypot(e1[0], e1[1]) || 1
    const l2 = Math.hypot(e2[0], e2[1]) || 1
    // Inward (left-hand) normals; at a reflex corner their sum points into the open space.
    const nx = -e1[1] / l1 - e2[1] / l2
    const nz = e1[0] / l1 + e2[0] / l2
    const m = Math.hypot(nx, nz) || 1
    out.push({ key: `room#${i}`, p: [p[0] + (nx / m) * EPS_NODE, p[1] + (nz / m) * EPS_NODE], owner: null })
  }
  return out
}

const dist2 = (a: Pt, b: Pt) => Math.hypot(a[0] - b[0], a[1] - b[1])

/** Axis-aligned box of a plan outline: [minX, minZ, maxX, maxZ]. */
function planBox(c: Pt[]): [number, number, number, number] {
  let minX = Infinity
  let minZ = Infinity
  let maxX = -Infinity
  let maxZ = -Infinity
  for (const [x, z] of c) {
    minX = Math.min(minX, x)
    maxX = Math.max(maxX, x)
    minZ = Math.min(minZ, z)
    maxZ = Math.max(maxZ, z)
  }
  return [minX, minZ, maxX, maxZ]
}

/** Is any part of the outline inside the ellipse of points whose detour via S and L is short (a generous corridor)? */
function inCorridor(c: Pt[], S: Pt, L: Pt, reach: number): boolean {
  return c.some((p) => dist2(S, p) + dist2(p, L) < 1.8 * reach + 25)
}

export interface RouteInput {
  obstacles: Obstacle[]
  room: RoomInput
  source: V3
  listener: V3
}

/** Shortest plan-view route from S to L that bends only round the given nodes; null when none exists. */
function shortestRoute(nodes: Node[], clear: (a: Pt, b: Pt) => boolean, S: Pt, L: Pt, banned: Set<string>): Node[] | null {
  const pts = [{ key: 'S', p: S, owner: null } as Node, ...nodes.filter((n) => !banned.has(n.key)), { key: 'L', p: L, owner: null } as Node]
  const n = pts.length
  const dist = new Array<number>(n).fill(Infinity)
  const prev = new Array<number>(n).fill(-1)
  const done = new Array<boolean>(n).fill(false)
  dist[0] = 0
  for (let step = 0; step < n; step++) {
    let u = -1
    for (let i = 0; i < n; i++) if (!done[i] && (u < 0 || dist[i]! < dist[u]!)) u = i
    if (u < 0 || dist[u] === Infinity) break
    done[u] = true
    if (u === n - 1) break
    for (let v = 0; v < n; v++) {
      if (done[v]) continue
      const d = dist[u]! + dist2(pts[u]!.p, pts[v]!.p)
      if (d < dist[v]! && clear(pts[u]!.p, pts[v]!.p)) {
        dist[v] = d
        prev[v] = u
      }
    }
  }
  if (!Number.isFinite(dist[n - 1]!)) return null
  const route: Node[] = []
  for (let i = n - 1; i >= 0; i = prev[i]!) {
    route.unshift(pts[i]!)
    if (i === 0) break
  }
  return route
}

/**
 * Diffraction routes for a blocked direct path: up to two ways round the obstacles (one per side) and one over the top.
 * Returns [] when the plan-view line of sight is clear.
 */
export function diffractionRoutes(input: RouteInput): DiffractionRoute[] {
  const { room, source, listener } = input
  const S: Pt = [source[0], source[2]]
  const L: Pt = [listener[0], listener[2]]
  const yLo = Math.min(source[1], listener[1]) - 0.3
  const yHi = Math.max(source[1], listener[1]) + 0.3
  const reach = Math.hypot(S[0] - L[0], S[1] - L[1])
  // Only obstacles in the corridor between source and listener can matter: skip everything else up front.
  const relevant = input.obstacles.filter((o) => o.yMax >= yLo && o.yMin <= yHi && inCorridor(o.plan, S, L, reach))
  const direct3D = Math.hypot(S[0] - L[0], S[1] - L[1], source[1] - listener[1])

  const boxes = new Map(relevant.map((o) => [o, planBox(o.plan)]))
  const crosses = (o: Obstacle, a: Pt, b: Pt) => {
    const bx = boxes.get(o)!
    if (Math.max(a[0], b[0]) < bx[0] || Math.min(a[0], b[0]) > bx[2] || Math.max(a[1], b[1]) < bx[1] || Math.min(a[1], b[1]) > bx[3])
      return false
    return segmentSpans([o.plan], a[0], a[1], b[0], b[1]).some(([t0, t1]) => t1 - t0 > 1e-7)
  }
  const insideRoom = (a: Pt, b: Pt) => {
    if (shapeOf(room) !== 'polygon') return true
    const spans = segmentSpans([outlineOf(room)], a[0], a[1], b[0], b[1])
    return spans.reduce((s, [t0, t1]) => s + (t1 - t0), 0) > 1 - 1e-6
  }
  const clear = (a: Pt, b: Pt) => insideRoom(a, b) && !relevant.some((o) => crosses(o, a, b))

  const blockers = relevant.filter((o) => crosses(o, S, L))
  const roomBlocked = !insideRoom(S, L)
  if (!blockers.length && !roomBlocked) return []

  const routes: DiffractionRoute[] = []

  // ---- Round the sides ------------------------------------------------------------------------
  const corridor = (p: Pt) => dist2(S, p) + dist2(p, L) < 1.8 * dist2(S, L) + 25
  // Corners outside the room are not places sound can be: a route may only bend round corners inside it.
  const inRoom = (p: Pt) => roomContains(room, [p[0], (yLo + yHi) / 2, p[1]])
  const nodes = [...relevant.flatMap(obstacleNodes), ...roomNodes(room)].filter((nd) => corridor(nd.p) && inRoom(nd.p)).slice(0, 48)
  const banned = new Set<string>()
  for (let attempt = 0; attempt < 2; attempt++) {
    const route = shortestRoute(nodes, clear, S, L, banned)
    if (!route || route.length < 3) break
    const plan = route.map((n) => n.p)
    let total = 0
    const cum = [0]
    for (let i = 1; i < plan.length; i++) {
      total += dist2(plan[i - 1]!, plan[i]!)
      cum.push(total)
    }
    const dy = listener[1] - source[1]
    const pts3: V3[] = []
    let valid = true
    for (let i = 1; i < plan.length - 1; i++) {
      const y = source[1] + (total > 0 ? dy * (cum[i]! / total) : 0)
      const owner = route[i]!.owner
      if (owner && (y > owner.yMax + 0.05 || y < owner.yMin - 0.05)) valid = false // the edge does not reach that high: goes over instead
      pts3.push([plan[i]![0], y, plan[i]![1]])
    }
    const length = Math.hypot(total, dy)
    for (const n of route.slice(1, -1)) banned.add(n.key)
    if (valid) {
      routes.push({
        kind: 'around',
        id: `diff:around:${route
          .slice(1, -1)
          .map((n) => n.key)
          .join('+')}`,
        label: `Round ${[...new Set(route.slice(1, -1).map((n) => n.owner?.name ?? 'room corner'))].join(' + ')}`,
        points: pts3,
        length,
        delta: Math.max(0, length - direct3D),
      })
    }
  }

  // ---- Over the top --------------------------------------------------------------------------
  const ceiling = room.size[1]
  if (blockers.length) {
    const tallest = blockers.reduce((a, b) => (b.yMax > a.yMax ? b : a))
    const topY = tallest.yMax + 0.02
    if (topY < ceiling - 0.05) {
      const spans = segmentSpans([tallest.plan], S[0], S[1], L[0], L[1])
      if (spans.length) {
        const t = spans.reduce((s, [a, b]) => s + (a + b) / 2, 0) / spans.length
        const apex: V3 = [S[0] + (L[0] - S[0]) * t, topY, S[1] + (L[1] - S[1]) * t]
        const length =
          Math.hypot(apex[0] - source[0], apex[1] - source[1], apex[2] - source[2]) +
          Math.hypot(apex[0] - listener[0], apex[1] - listener[1], apex[2] - listener[2])
        routes.push({
          kind: 'over',
          id: `diff:over:${tallest.id}`,
          label: `Over ${tallest.name}`,
          points: [apex],
          length,
          delta: Math.max(0, length - direct3D),
        })
      }
    }
  }
  return routes
}
